<template>
  <!-- Direction: LTR by default in every locale (numeric slider). Authors can
       opt into RTL mirroring per-instance via the `flipOnRtl` field — when set
       AND the language is RTL, the stops/handle mirror to read right→left and
       the pointer→ratio map + left/right keys invert to match. We force
       dir="ltr" unless flipping, so the page-level dir="rtl" can't mirror us by
       default. Mirroring is value-only (stopLeft / handleLeft); the physical
       `translateX(-50%)` centering is untouched. -->
  <div class="stepped-slider" :style="rootStyle" :dir="dirAttr">
    <div
      ref="trackRef"
      class="stepped-slider__track click-passthrough-region"
      role="slider"
      :tabindex="0"
      :aria-valuemin="ariaValueMin"
      :aria-valuemax="ariaValueMax"
      :aria-valuenow="value"
      :aria-valuetext="ariaValueText"
      @pointerdown="onTrackPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @keydown.left.prevent="stepH(-1)"
      @keydown.right.prevent="stepH(1)"
      @keydown.down.prevent="step(-1)"
      @keydown.up.prevent="step(1)"
      @keydown.page-down.prevent="step(-1)"
      @keydown.page-up.prevent="step(1)"
      @keydown.home.prevent="goTo(0)"
      @keydown.end.prevent="goTo(stops.length - 1)"
    >
      <div class="stepped-slider__rail" />
      <template v-for="(stop, index) in stops" :key="stop.id">
        <div
          class="stepped-slider__stop"
          :class="{ 'stepped-slider__stop--hidden': index === snappedIndex }"
          :style="{ left: stopLeft(index) }"
        >
          <PlainImage
            v-if="stopSpriteFor(stop, index)"
            :image="stopSpriteFor(stop, index)"
            class="stepped-slider__stop-sprite"
          />
          <div v-else class="stepped-slider__stop-bar" />
        </div>
      </template>
      <div
        class="stepped-slider__handle"
        :class="{ 'stepped-slider__handle--dragging': dragRatio !== null }"
        :style="{ left: handleLeft }"
        aria-hidden="true"
      >
        <PlainImage v-if="fields.handleSprite?.id" :image="fields.handleSprite" />
        <div v-else class="stepped-slider__handle-default">
          <svg
            viewBox="0 0 64 40"
            class="stepped-slider__handle-chevrons"
            focusable="false"
            aria-hidden="true"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              d="M17.5 14 L13 20 L17.5 26 M46.5 14 L51 20 L46.5 26"
              :stroke="handleArrowStrokeColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
        </div>
      </div>
    </div>
    <div class="stepped-slider__labels">
      <div class="stepped-slider__label stepped-slider__label--start">
        <TipTap v-model="startLabelValue" mode="single" />
      </div>
      <div class="stepped-slider__label stepped-slider__label--end">
        <TipTap v-model="endLabelValue" mode="single" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { isInPipeContext, usePipe } from '@render/composables/usePipe'
import { useDirection } from '@render/composables/useDirection'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import TipTap from '@render/lib/TipTap'
import PlainImage from '@render/components/generic/PlainImage.vue'
import type { SteppedSliderStop } from '@render/types/steppedSliderStops'
import type { ImageFieldData } from '@render/composables/useImagePreset'
import type { SteppedSliderFields as Fields } from './definition'

const fields = defineModel<Fields>('fields', {
  required: true,
  default: (): Fields => ({}),
})

const pipe = isInPipeContext() ? usePipe() : null

const { isRtl, dir } = useDirection()

// Mirror only when the author opted in (`flipOnRtl`) AND the language is RTL.
// Otherwise the slider stays LTR — including the forced root `dir`.
const flipRtl = computed(() => isRtl.value && fields.value.flipOnRtl === true)
const dirAttr = computed<'rtl' | 'ltr'>(() => (fields.value.flipOnRtl ? dir.value : 'ltr'))

// ---------- stops + value ----------

const stops = computed<SteppedSliderStop[]>(() => fields.value.stops?.stops ?? [])

const value = computed<number>({
  get() {
    const key = fields.value.valueVariable?.key
    const stored = key && pipe ? pipe.getData(`var.${key}`) : undefined
    if (typeof stored === 'number') return stored
    if (typeof fields.value.defaultValue === 'number') return fields.value.defaultValue
    return stops.value[0]?.value ?? 0
  },
  set(v) {
    const key = fields.value.valueVariable?.key
    if (!key || !pipe) return
    pipe.setData(`var.${key}`, v)
  },
})

// On load, if the stored value doesn't match any stop value, reset it to
// the default (or the first stop). `pipe.whenReady()` is required so the
// boot-time localStorage→variables merge has flushed before we read.
onMounted(async () => {
  if (!pipe) return
  await pipe.whenReady()
  const key = fields.value.valueVariable?.key
  if (!key) return
  const stored = pipe.getData(`var.${key}`)
  const stopValues = stops.value.map((s) => s.value)
  if (typeof stored === 'number' && stopValues.includes(stored)) return
  const fallback =
    typeof fields.value.defaultValue === 'number' ? fields.value.defaultValue : stopValues[0]
  if (fallback === undefined) return
  pipe.setData(`var.${key}`, fallback)
})

// Snap-to-nearest index without rewriting the variable on every render.
const activeIndex = computed<number>(() => {
  if (stops.value.length === 0) return 0
  let bestIdx = 0
  let bestDelta = Infinity
  for (let i = 0; i < stops.value.length; i++) {
    const d = Math.abs(stops.value[i]!.value - value.value)
    if (d < bestDelta) {
      bestDelta = d
      bestIdx = i
    }
  }
  return bestIdx
})

const ariaValueMin = computed(() => stops.value[0]?.value ?? 0)
const ariaValueMax = computed(() => stops.value[stops.value.length - 1]?.value ?? 0)
const ariaValueText = computed(() => {
  const stop = stops.value[activeIndex.value]
  return stop ? String(stop.value) : ''
})

// ---------- positions ----------

const trackRef = ref<HTMLElement | null>(null)
// `dragRatio` is the continuous fractional position of the handle while
// the user is dragging (0 = first stop, 1 = last stop). Null when not
// dragging. The handle follows the cursor freely; we only snap to the
// nearest stop on pointerup.
const dragRatio = ref<number | null>(null)

// Index of the stop currently hidden under the handle. We delay setting
// this until after the handle's CSS transition completes so the stop
// doesn't disappear mid-flight. While dragging the handle is loose and
// no stop is hidden (snappedIndex = -1).
const HANDLE_TRANSITION_MS = 200
const snappedIndex = ref<number>(-1)
let snapTimer: ReturnType<typeof setTimeout> | null = null

function clearSnapTimer() {
  if (snapTimer !== null) {
    clearTimeout(snapTimer)
    snapTimer = null
  }
}

onBeforeUnmount(clearSnapTimer)

// Drive `snappedIndex`. Initial mount: hide immediately (no animation
// runs since the handle starts at the active stop position). Transitions
// (drag release, keyboard step, programmatic value change): wait the
// transition duration before hiding so the handle visibly slides over
// the stop instead of the stop disappearing mid-flight. While dragging:
// no stop is hidden — keeps every snap target visible to the user.
snappedIndex.value = activeIndex.value
watch(
  () => [dragRatio.value, activeIndex.value] as const,
  ([drag, idx]) => {
    clearSnapTimer()
    if (drag !== null) {
      snappedIndex.value = -1
      return
    }
    snapTimer = setTimeout(() => {
      snappedIndex.value = idx
      snapTimer = null
    }, HANDLE_TRANSITION_MS)
  },
)

function ratioFromPointer(clientX: number): number {
  const el = trackRef.value
  if (!el) return 0
  const rect = el.getBoundingClientRect()
  if (rect.width <= 0) return 0
  // Map pointer-X to handle-center range. The handle's center can travel
  // from `handleWidth/2` to `width - handleWidth/2` (Fix 2 above), and we
  // want pointer X = leftmost edge to mean ratio 0, pointer X = rightmost
  // edge to mean ratio 1. Mapping over the same effective range here keeps
  // the handle directly under the cursor instead of lagging behind by half
  // a handle width when the user grabs near an edge.
  const handlePx =
    trackRef.value?.querySelector<HTMLElement>('.stepped-slider__handle')?.offsetWidth ?? 0
  const usable = Math.max(1, rect.width - handlePx)
  const x = clientX - rect.left - handlePx / 2
  const raw = x / usable
  // Logical ratio: 0 = first stop, 1 = last stop. When mirrored the track reads
  // right→left, so a pointer near the physical-left edge maps to ratio 1.
  return Math.max(0, Math.min(1, flipRtl.value ? 1 - raw : raw))
}

function indexFromRatio(ratio: number): number {
  if (stops.value.length <= 1) return 0
  return Math.round(ratio * (stops.value.length - 1))
}

function stopLeft(index: number): string {
  if (stops.value.length <= 1) return '0%'
  const pct = (index / (stops.value.length - 1)) * 100
  return `${flipRtl.value ? 100 - pct : pct}%`
}

// Constrain the handle to live fully inside the track: at ratio 0 its left
// edge aligns with the track's left edge (so its center sits at handleWidth/2),
// at ratio 1 its right edge aligns with the track's right edge (center at
// 100% - handleWidth/2). Stops keep their natural 0..100% positions; the
// stop nearest the current handle position (snap target) is hidden so the
// visual matches the Figma at rest and previews the snap target during drag.
const handleRatio = computed<number>(() => {
  if (dragRatio.value !== null) return dragRatio.value
  if (stops.value.length <= 1) return 0
  return activeIndex.value / (stops.value.length - 1)
})

const handleLeft = computed<string>(() => {
  const handleWidth = handleWidthCss.value ?? '64px'
  // Mirror the physical position when flipping (logical ratio r → 1 - r from
  // the left), keeping the `translateX(-50%)` centering physical.
  const r = flipRtl.value ? 1 - handleRatio.value : handleRatio.value
  return `calc(${r} * (100% - ${handleWidth}) + ${handleWidth} / 2)`
})

function stopSpriteFor(stop: SteppedSliderStop, index: number): ImageFieldData | undefined {
  if (stop.sprite?.id) return stop.sprite
  if (index === 0 || index === stops.value.length - 1) return undefined
  return fields.value.middleStopSprite
}

// ---------- interaction ----------

function commit(idx: number) {
  const stop = stops.value[idx]
  if (!stop) return
  if (stop.value !== value.value) value.value = stop.value
}

function step(delta: number) {
  if (stops.value.length === 0) return
  const next = Math.max(0, Math.min(stops.value.length - 1, activeIndex.value + delta))
  commit(next)
}

// Horizontal arrow keys: `physical` is +1 for ArrowRight, -1 for ArrowLeft.
// When mirrored the visual order is reversed, so a physical-right press moves
// to the previous (lower-index) stop and vice versa.
function stepH(physical: number) {
  step(flipRtl.value ? -physical : physical)
}

function goTo(idx: number) {
  if (idx < 0 || idx >= stops.value.length) return
  commit(idx)
}

function onTrackPointerDown(e: PointerEvent) {
  const el = trackRef.value
  if (!el) return
  el.setPointerCapture?.(e.pointerId)
  dragRatio.value = ratioFromPointer(e.clientX)
}

function onPointerMove(e: PointerEvent) {
  if (dragRatio.value === null) return
  dragRatio.value = ratioFromPointer(e.clientX)
}

function onPointerUp(e: PointerEvent) {
  const el = trackRef.value
  if (!el) return
  el.releasePointerCapture?.(e.pointerId)
  if (dragRatio.value !== null) {
    commit(indexFromRatio(dragRatio.value))
    dragRatio.value = null
  }
}

// ---------- translations ----------

function translationModel(keyPath: () => string | undefined) {
  return computed<object>({
    get: () => {
      const key = keyPath()
      if (!key || !pipe) return {}
      return pipe.getTranslation(key) ?? {}
    },
    set: (val) => {
      const key = keyPath()
      if (!key || !pipe) return
      pipe.setTranslation(key, val)
    },
  })
}

const startLabelValue = translationModel(() => fields.value.startLabel?.key)
const endLabelValue = translationModel(() => fields.value.endLabel?.key)

// ---------- style resolution ----------

const trackColorCss = computed(() => getColorStyleData(fields.value.trackColor, '#AEDFF4'))
const trackHeightCss = computed(() => getSizeStyleData(fields.value.trackHeight, '6px'))
const handleColorCss = computed(() => getColorStyleData(fields.value.handleColor, '#29ABE2'))
const handleArrowColorCss = computed(() =>
  getColorStyleData(fields.value.handleArrowColor, '#FFFFFF'),
)
const handleWidthCss = computed(() => getSizeStyleData(fields.value.handleWidth, '64px'))
const handleHeightCss = computed(() => getSizeStyleData(fields.value.handleHeight, '40px'))
const stopColorCss = computed(() => getColorStyleData(fields.value.stopColor, '#AEDFF4'))
const stopWidthCss = computed(() => getSizeStyleData(fields.value.stopWidth, '6px'))
const stopHeightCss = computed(() => getSizeStyleData(fields.value.stopHeight, '24px'))
const labelColorCss = computed(() =>
  getColorStyleData(fields.value.labelColor, 'var(--color-text, #769792)'),
)
const labelFontSizeCss = computed(() =>
  getSizeStyleData(fields.value.labelFontSize, 'var(--text-body-sm-size, 14px)'),
)
const labelFontFamilyCss = computed(() => fields.value.labelFontFamily || 'inherit')
const labelFontWeightCss = computed(() => String(fields.value.labelFontWeight ?? 'inherit'))
const labelSpacingCss = computed(() => getSizeStyleData(fields.value.labelSpacing, '12px'))
const marginCss = computed(() => getSpacingStyleData(fields.value.margin, '0'))

// Pure SVG fill values (no v-bind targets so they can drive `fill` / `stroke`
// directly inside the inline <svg> default handle).
const handleFillColor = computed(() => handleColorCss.value ?? '#29ABE2')
const handleArrowStrokeColor = computed(() => handleArrowColorCss.value ?? '#FFFFFF')

const rootStyle = computed(() => ({
  margin: marginCss.value ?? undefined,
}))
</script>

<style scoped lang="scss">
.stepped-slider {
  display: flex;
  flex-direction: column;
  width: 100%;
  user-select: none;
}

.stepped-slider__track {
  position: relative;
  width: 100%;
  // Vertical room for the handle (which extends above and below the rail).
  height: v-bind(handleHeightCss);
  cursor: pointer;
  outline: none;
  // Required for horizontal drag to work on Android Chrome.
  touch-action: pan-y;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--color-primary-300, #aedff4);
    border-radius: 4px;
  }
}

.stepped-slider__rail {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: v-bind(trackHeightCss);
  background: v-bind(trackColorCss);
  border-radius: 9999px;
  pointer-events: none;
}

.stepped-slider__stop {
  position: absolute;
  top: 50%;
  transform: translate3d(-50%, -50%, 0);
  backface-visibility: hidden;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.stepped-slider__stop--hidden {
  visibility: hidden;
}

.stepped-slider__stop-bar {
  width: v-bind(stopWidthCss);
  height: v-bind(stopHeightCss);
  background: v-bind(stopColorCss);
  border-radius: 9999px;
}

.stepped-slider__stop-sprite {
  height: v-bind(stopHeightCss);
  width: auto;
  object-fit: contain;
}

.stepped-slider__stop-sprite--flipped {
  transform: scaleX(-1);
}

.stepped-slider__handle {
  position: absolute;
  top: 50%;
  width: v-bind(handleWidthCss);
  height: v-bind(handleHeightCss);
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  // Snap-on-release: handle slides to the nearest stop after pointerup.
  // The matching JS delay (HANDLE_TRANSITION_MS) drives when the stop
  // under the handle becomes hidden — keep the two in sync.
  transition: left 200ms ease;
}

.stepped-slider__handle--dragging {
  // No transition while the user is dragging — handle should track the
  // pointer instantly.
  transition: none;
}

.stepped-slider__handle-default {
  width: 100%;
  height: 100%;
  background: v-bind(handleFillColor);
  border-radius: 30px;
  box-shadow: 0 2px 4px rgba(20, 111, 149, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepped-slider__handle-chevrons {
  width: 100%;
  height: 100%;
  display: block;
}

.stepped-slider__labels {
  display: flex;
  justify-content: space-between;
  margin-top: v-bind(labelSpacingCss);
  color: v-bind(labelColorCss);
  font-size: v-bind(labelFontSizeCss);
  font-family: v-bind(labelFontFamilyCss);
  font-weight: v-bind(labelFontWeightCss);
}

.stepped-slider__label--start {
  text-align: start;
}

.stepped-slider__label--end {
  text-align: end;
}
</style>
